import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import {
  MarketShopOrderCartItem,
  MarketShopOrderStatus,
  MarketShopOrderStatusEnum,
  MarketShopTransactionModel,
} from '@/models'

export interface GetMarketShopTransactionsRequest {
  buildingId?: number
  skip?: number
  limit?: number
  fromDate?: number
  toDate?: number
  search?: string
  status?: MarketShopOrderStatus
  sort?: string
}

interface ApiTransaction extends Omit<MarketShopTransactionModel, 'orderSummary'> {}

interface ApiResponse {
  total: number
  data: ApiTransaction[]
  countNew?: number
  countInProgress?: number
  countAwaitingPickup?: number
  countCompleted?: number
  countDeclined?: number
  marketShopRestaurantAvailable?: boolean
}

export interface GetMarketShopTransactionsResponse {
  total: number
  data: MarketShopTransactionModel[]
  countByStatus?: Partial<Record<MarketShopOrderStatus, number>>
  marketShopRestaurantAvailable?: boolean
}

const formatOrderSummary = (orderCart: MarketShopOrderCartItem[]): string => {
  const itemSummaries = orderCart
    .filter((item) => item.quantity > 0 && item.summary)
    .map((item) => {
      const { quantity, summary } = item
      return quantity === 1 ? summary : `${quantity}x ${summary}`
    })

  return itemSummaries.join(', ')
}

const statusToCountKey = {
  awaiting_pickup: 'countAwaitingPickup',
  completed: 'countCompleted',
  declined: 'countDeclined',
  in_progress: 'countInProgress',
  new: 'countNew',
} as const satisfies Record<MarketShopOrderStatus, keyof ApiResponse>

const transformResponse = (response: ApiResponse): GetMarketShopTransactionsResponse => {
  const counts = MarketShopOrderStatusEnum.options
    .filter((status) => response[statusToCountKey[status]] !== undefined)
    .map((status) => [status, response[statusToCountKey[status]]] as const)

  return {
    countByStatus:
      counts.length > 0
        ? (Object.fromEntries(counts) as Partial<Record<MarketShopOrderStatus, number>>)
        : undefined,
    data: response.data.map((transaction) => ({
      ...transaction,
      orderSummary: formatOrderSummary(transaction.orderCart),
    })),
    marketShopRestaurantAvailable: response.marketShopRestaurantAvailable,
    total: response.total,
  }
}

export const MARKET_SHOP_TRANSACTIONS_BASE_QUERY_KEY = 'useGetMarketShopTransactions'

export const getMarketShopTransactionsQueryKey = (data: GetMarketShopTransactionsRequest) => [
  MARKET_SHOP_TRANSACTIONS_BASE_QUERY_KEY,
  data,
]

export const getMarketShopTransactions = async (
  data: GetMarketShopTransactionsRequest,
  signal?: AbortSignal,
) => {
  const response = await authAPI.get<ApiResponse>('/market-shop/transactions', {
    params: data,
    signal,
  })
  return transformResponse(response.data)
}

export const useGetMarketShopTransactions = (data: GetMarketShopTransactionsRequest) => {
  return useQuery({
    enabled: !!data.buildingId,
    gcTime: 0,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getMarketShopTransactions({ ...data }, signal),
    queryKey: getMarketShopTransactionsQueryKey(data),
  })
}
